import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  InputAdornment,
  useTheme,
  useMediaQuery
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  AddOutlined as AddIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  DescriptionOutlined as DescriptionIcon,
  PersonOutline as PersonIcon,
  AttachMoneyOutlined as MoneyIcon,
  CalendarTodayOutlined as CalendarIcon,
  WarningAmberOutlined as WarningIcon,
  CheckCircleOutline as CheckCircleIcon
} from '@mui/icons-material';
import api from '../api';
import { isFinancialsEnabled } from '../config/featureFlags';
import { CreateRentLedgerDialog } from './financials/CreateRentLedgerDialog';
import { stashLedgerNav } from './financials/api';
import EntityDocumentsStrip from './EntityDocumentsStrip';
import LeaseIntakeDialog from './leaseIntake/LeaseIntakeDialog';
import { listLeaseIntakes } from './leaseIntake/api';

const LeaseManagement = ({ onGoToFinancials, onOpenDocuments }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [leases, setLeases] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLease, setEditingLease] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rentIncreasePercent, setRentIncreasePercent] = useState(0);
  const [includeMonthToMonth, setIncludeMonthToMonth] = useState(true);
  const [sendingRenewal, setSendingRenewal] = useState(false);
  const [testingReminder, setTestingReminder] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [ledgerLease, setLedgerLease] = useState(null);
  const [leaseIntakes, setLeaseIntakes] = useState(null);
  const [intakeDialog, setIntakeDialog] = useState({ open: false, resumeId: null });
  const finEnabled = isFinancialsEnabled();

  // Form state
  const [formData, setFormData] = useState({
    propertyId: '',
    unitId: '',
    tenantId: '',
    startDate: '',
    endDate: '',
    monthlyRent: '',
    securityDeposit: '',
    lateFee: '',
    leaseDocument: '',
    status: 'active',
    notes: ''
  });

  useEffect(() => {
    fetchLeases();
    fetchProperties();
    fetchLeaseIntakes();
  }, []);

  /** Null when adding from a signed lease is off for this account (the API answers 404). */
  const fetchLeaseIntakes = async () => {
    try {
      setLeaseIntakes(await listLeaseIntakes());
    } catch (err) {
      setLeaseIntakes(null);
    }
  };

  const handleLeaseFromIntake = (lease) => {
    setIntakeDialog({ open: false, resumeId: null });
    fetchLeases();
    const where = lease?.unitName ? ` for ${lease.unitName}` : '';
    const next = finEnabled ? ' You can set up its rent ledger from the lease list.' : '';
    setSnackbar({ open: true, message: `Lease created${where}. No invitation was sent.${next}`, severity: 'success' });
  };

  // Refresh tenant list when properties load (for leaseInfo labels)
  useEffect(() => {
    fetchTenants();
  }, [properties]);

  const fetchLeases = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/leases');
      setLeases(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      console.log('🔍 Fetching properties...');
      const response = await api.get('/api/properties');
      console.log('✅ Properties fetched:', response.data);
      console.log('🔍 First property structure:', response.data[0]);
      console.log('🔍 First property units:', response.data[0]?.units);
      setProperties(response.data);
    } catch (err) {
      console.error('❌ Error fetching properties:', err);
    }
  };

  const tenantDisplayName = (tenant) => {
    if (tenant.name) return tenant.name;
    const full = `${tenant.firstName || ''} ${tenant.lastName || ''}`.trim();
    return full || tenant.email || 'Tenant';
  };

  const resolveTenantContext = (tenant) => {
    const pid = tenant.leaseInfo?.propertyId || tenant.propertyId;
    const uid = tenant.leaseInfo?.unitId || tenant.unitId;
    const property = pid ? properties.find((p) => p.id === pid) : null;
    const unit = property?.units?.find((u) => u.id === uid);
    return {
      propertyName: property?.name || (pid ? 'Property' : 'Not linked yet'),
      unitName: unit?.name || (uid ? 'Unit' : '—'),
    };
  };

  const fetchTenants = async () => {
    try {
      console.log('🔍 Fetching tenants from API...');
      const response = await api.get('/api/tenants');
      const list = Array.isArray(response.data) ? response.data : [];
      const enriched = list
        .filter((t) => t.id)
        .map((tenant) => {
          const ctx = resolveTenantContext(tenant);
          return {
            ...tenant,
            propertyName: ctx.propertyName,
            unitName: ctx.unitName,
          };
        });
      console.log('✅ Tenants fetched:', enriched);
      setTenants(enriched);
    } catch (err) {
      console.error('❌ Error fetching tenants:', err);
      setTenants([]);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = async (lease = null) => {
    await fetchTenants();
    if (lease) {
      setEditingLease(lease);
      setRentIncreasePercent(0);
      setFormData({
        propertyId: lease.propertyId,
        unitId: lease.unitId,
        tenantId: lease.tenantId,
        startDate: lease.startDate,
        endDate: lease.endDate,
        monthlyRent: lease.monthlyRent,
        securityDeposit: lease.securityDeposit,
        lateFee: lease.lateFee,
        leaseDocument: lease.leaseDocument,
        status: lease.status,
        notes: lease.notes
      });
    } else {
      setEditingLease(null);
      setRentIncreasePercent(0);
      setFormData({
        propertyId: '',
        unitId: '',
        tenantId: '',
        startDate: '',
        endDate: '',
        monthlyRent: '',
        securityDeposit: '',
        lateFee: '',
        leaseDocument: '',
        status: 'active',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLease(null);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Clear unitId when property changes
      if (field === 'propertyId') {
        newData.unitId = '';
      }
      
      return newData;
    });
  };

  const handleSubmit = async () => {
    if (submitting) return; // Prevent double submission
    
    try {
      setSubmitting(true);
      setError(null); // Clear previous errors
      
      console.log('🔍 Form data being submitted:', formData);
      console.log('🔍 Properties available:', properties);
      console.log('🔍 Tenants available:', tenants);
      
      // Validate required fields with detailed logging
      const missingFields = [];
      if (!formData.propertyId) missingFields.push('Property');
      if (!formData.unitId) missingFields.push('Unit');
      if (!formData.tenantId || !String(formData.tenantId).trim()) missingFields.push('Tenant');
      if (!formData.startDate) missingFields.push('Start Date');
      
      if (missingFields.length > 0) {
        console.log('❌ Missing required fields:', missingFields);
        setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
        return;
      }

      // Validate dates (no end date means month-to-month)
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      if (formData.endDate && startDate >= endDate) {
        setError('End date must be after start date');
        return;
      }

      // Validate rent amount
      if (formData.monthlyRent && (isNaN(formData.monthlyRent) || formData.monthlyRent <= 0)) {
        setError('Monthly rent must be a positive number');
        return;
      }

      console.log('✅ Validation passed, submitting lease data:', formData);
      
      // Get property and unit names for the backend
      const selectedProperty = properties.find(p => p.id === formData.propertyId);
      const selectedUnit = selectedProperty?.units?.find(u => u.id === formData.unitId);
      const selectedTenant = tenants.find(t => t.id === formData.tenantId);
      
      const leaseData = {
        ...formData,
        propertyName: selectedProperty?.name || '',
        unitName: selectedUnit?.name || '',
        tenantName: selectedTenant ? tenantDisplayName(selectedTenant) : ''
      };
      
      console.log('📝 Final lease data with names:', leaseData);
      
      const url = editingLease 
        ? `/api/leases/${editingLease.id}`
        : '/api/leases';
      
      const response = editingLease 
        ? await api.put(url, leaseData)
        : await api.post(url, leaseData);

      console.log('✅ Lease response:', response.data);
      handleCloseDialog();
      fetchLeases();
    } catch (err) {
      console.error('❌ Lease submission error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save lease';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendRenewalNotice = async () => {
    if (!editingLease?.id) return;
    setSendingRenewal(true);
    setError(null);
    try {
      const res = await api.post(`/api/leases/send-renewal-notice/${editingLease.id}`, {
        rentIncreasePercent: Number(rentIncreasePercent) || 0,
        includeMonthToMonth
      });
      const sent = res.data?.sent || {};
      const parts = [];
      if (sent.tenantEmail) parts.push('tenant email');
      if (sent.tenantSms) parts.push('tenant SMS');
      if (sent.managerEmail) parts.push('manager email');
      if (sent.managerSms) parts.push('manager SMS');
      const message = parts.length > 0
        ? `Renewal notice sent (${parts.join(', ')})`
        : 'Renewal notice saved. No email/SMS sent - check tenant contact info, Settings > Manager Phone/Email, and Twilio/Resend config.';
      setSnackbar({ open: true, message, severity: parts.length > 0 ? 'success' : 'warning' });
      handleCloseDialog();
      fetchLeases();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send renewal notice');
    } finally {
      setSendingRenewal(false);
    }
  };

  /** Leases without an end date are month-to-month. */
  const formatLeaseDay = (day) => (day ? new Date(`${day}T00:00:00`).toLocaleDateString() : 'Month-to-month');

  const getDaysUntilExpiry = (endDate) => {
    const today = new Date();
    const expiry = new Date(endDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryStatus = (endDate) => {
    const daysUntilExpiry = getDaysUntilExpiry(endDate);
    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 30) return 'expiring-soon';
    return 'active';
  };

  const isRenewalEligible = editingLease && (
    getExpiryStatus(editingLease.endDate) === 'expiring-soon' ||
    editingLease.status === 'renewal' ||
    editingLease.status === 'pending'
  );
  const currentRent = Number(editingLease?.monthlyRent) || 0;
  const calculatedNewRent = currentRent * (1 + (Number(rentIncreasePercent) || 0) / 100);

  const handleTriggerManagerReminder = async () => {
    setTestingReminder(true);
    try {
      const res = await api.post('/api/leases/trigger-manager-reminders?force=true');
      setSnackbar({
        open: true,
        message: res.data.sent ? 'Manager reminder sent to your phone!' : 'No expiring leases or manager phone not set.',
        severity: res.data.sent ? 'success' : 'info'
      });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to send', severity: 'error' });
    } finally {
      setTestingReminder(false);
    }
  };

  const handleDelete = async (leaseId) => {
    if (window.confirm('Are you sure you want to delete this lease?')) {
      try {
        await api.delete(`/api/leases/${leaseId}`);
        fetchLeases();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'error';
      case 'pending': return 'warning';
      case 'renewal': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 3 },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4"
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: { xs: 1, sm: 0 } }}
        >
          Lease Management
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          {leaseIntakes && (
            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={() => setIntakeDialog({ open: true, resumeId: null })}
              fullWidth={isMobile}
              size={isMobile ? 'small' : 'medium'}
            >
              Add from signed lease
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? 'Add Lease' : 'Add New Lease'}
          </Button>
        </Box>
      </Box>

      {leaseIntakes?.intakes?.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: { xs: 2, sm: 3 } }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setIntakeDialog({ open: true, resumeId: leaseIntakes.intakes[0].id })}
            >
              Continue
            </Button>
          }
        >
          {leaseIntakes.intakes.length === 1
            ? 'A signed lease is waiting for you to check it.'
            : `${leaseIntakes.intakes.length} signed leases are waiting for you to check them.`}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 } }}>
          {error}
        </Alert>
      )}

      {leases.length === 0 && (
        <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
          Create a lease after you have a property/unit and tenant. See <strong>Home</strong> for setup steps.
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: { xs: 2, sm: 3 } }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant={isMobile ? 'scrollable' : 'standard'} scrollButtons="auto">
          <Tab label="Active Leases" />
          <Tab label="Expired Leases" />
          <Tab label="Pending Renewals" />
          <Tab label="All Leases" />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          {leases
            .filter(lease => lease.status === 'active')
            .map((lease) => (
              <Grid item xs={12} md={6} lg={4} key={lease.id}>
                <Card>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start', 
                      mb: { xs: 1.5, sm: 2 },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: 0 }
                    }}>
                      <Typography 
                        variant="h6" 
                        component="div"
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                      >
                        {lease.propertyName} - {lease.unitName}
                      </Typography>
                      <Chip
                        label={lease.status}
                        color={getStatusColor(lease.status)}
                        size="small"
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 0.75, sm: 1 } }}>
                      <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        {lease.tenantName}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 0.75, sm: 1 } }}>
                      <MoneyIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        ${lease.monthlyRent}/month
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
                      <CalendarIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        {formatLeaseDay(lease.startDate)} - {formatLeaseDay(lease.endDate)}
                      </Typography>
                    </Box>
                    
                    {getExpiryStatus(lease.endDate) === 'expiring-soon' && (
                      <Alert severity="warning" sx={{ mb: { xs: 1.5, sm: 2 } }}>
                        Expires in {getDaysUntilExpiry(lease.endDate)} days
                      </Alert>
                    )}
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: 0 }
                    }}>
                      <Typography 
                        variant="caption" 
                        color="textSecondary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      >
                        Security Deposit: ${lease.securityDeposit}
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        gap: { xs: 0.5, sm: 1 },
                        flexDirection: { xs: 'row', sm: 'row' }
                      }}>
                        <IconButton
                          size={isMobile ? 'small' : 'medium'}
                          onClick={() => handleOpenDialog(lease)}
                          aria-label="Edit lease"
                        >
                          <EditIcon fontSize={isMobile ? 'small' : 'medium'} />
                        </IconButton>
                        {finEnabled && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setLedgerLease(lease)}
                            sx={{ ml: 0.5 }}
                          >
                            Rent ledger
                          </Button>
                        )}
                        <IconButton
                          size={isMobile ? 'small' : 'medium'}
                          onClick={() => handleDelete(lease.id)}
                          color="error"
                          aria-label="Delete lease"
                        >
                          <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      )}

      {activeTab === 1 && (
        <TableContainer 
          component={Paper}
          sx={{ 
            overflowX: 'auto',
            maxWidth: { xs: '100%', sm: 'none' }
          }}
        >
          <Table sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Property</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Tenant</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>End Date</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Monthly Rent</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Status</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leases
                .filter(lease => lease.status === 'expired')
                .map((lease) => (
                  <TableRow key={lease.id}>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{lease.propertyName} - {lease.unitName}</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{lease.tenantName}</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{formatLeaseDay(lease.endDate)}</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>${lease.monthlyRent}</TableCell>
                    <TableCell>
                      <Chip
                        label={lease.status}
                        color={getStatusColor(lease.status)}
                        size="small"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.5 } }}>
                        <IconButton
                          size={isMobile ? 'small' : 'medium'}
                          onClick={() => handleOpenDialog(lease)}
                          aria-label="Edit lease"
                        >
                          <EditIcon fontSize={isMobile ? 'small' : 'medium'} />
                        </IconButton>
                        {finEnabled && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setLedgerLease(lease)}
                            sx={{ ml: 0.5 }}
                          >
                            Rent ledger
                          </Button>
                        )}
                        <IconButton
                          size={isMobile ? 'small' : 'medium'}
                          onClick={() => handleDelete(lease.id)}
                          color="error"
                          aria-label="Delete lease"
                        >
                          <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {activeTab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleTriggerManagerReminder}
              disabled={testingReminder}
            >
              {testingReminder ? 'Sending...' : 'Test Manager Reminder'}
            </Button>
          </Box>
          <List>
          {leases
            .filter(lease => lease.status === 'pending' || lease.status === 'renewal' || getExpiryStatus(lease.endDate) === 'expiring-soon')
            .map((lease) => (
              <ListItem 
                key={lease.id} 
                divider
                sx={{ 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  py: { xs: 1.5, sm: 1 }
                }}
              >
                <ListItemText
                  primary={`${lease.propertyName} - ${lease.unitName}`}
                  secondary={`${lease.tenantName} • ${lease.status === 'pending' || lease.status === 'renewal' ? `Status: ${lease.status}` : `Expires in ${getDaysUntilExpiry(lease.endDate)} days`}`}
                  sx={{ 
                    mb: { xs: 1, sm: 0 },
                    '& .MuiListItemText-primary': { fontSize: { xs: '0.9rem', sm: '1rem' } },
                    '& .MuiListItemText-secondary': { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                  }}
                />
                <ListItemSecondaryAction sx={{ position: { xs: 'relative', sm: 'absolute' }, right: { xs: 0, sm: 16 } }}>
                  <Button
                    variant="outlined"
                    size={isMobile ? 'small' : 'medium'}
                    onClick={() => handleOpenDialog(lease)}
                    fullWidth={isMobile}
                  >
                    {lease.status === 'pending' || lease.status === 'renewal' ? (isMobile ? 'Edit' : 'Edit Lease') : (isMobile ? 'Renew' : 'Renew Lease')}
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {activeTab === 3 && (
        <TableContainer 
          component={Paper}
          sx={{ 
            overflowX: 'auto',
            maxWidth: { xs: '100%', sm: 'none' }
          }}
        >
          <Table sx={{ minWidth: { xs: 700, sm: 'auto' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Property</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Tenant</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Start Date</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>End Date</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Monthly Rent</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Status</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leases.map((lease) => (
                <TableRow key={lease.id}>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{lease.propertyName} - {lease.unitName}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{lease.tenantName}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{formatLeaseDay(lease.startDate)}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{formatLeaseDay(lease.endDate)}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>${lease.monthlyRent}</TableCell>
                  <TableCell>
                    <Chip
                      label={lease.status}
                      color={getStatusColor(lease.status)}
                      size="small"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.5 } }}>
                      <IconButton
                        size={isMobile ? 'small' : 'medium'}
                        onClick={() => handleOpenDialog(lease)}
                        aria-label="Edit lease"
                      >
                        <EditIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                      <IconButton
                        size={isMobile ? 'small' : 'medium'}
                        onClick={() => handleDelete(lease.id)}
                        color="error"
                        aria-label="Delete lease"
                      >
                        <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Lease Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {editingLease ? 'Edit Lease Agreement' : 'Add New Lease Agreement'}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 1, sm: 1.5 }, pb: { xs: 2, sm: 2 } }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="lease-property-label">Property</InputLabel>
                <Select
                  labelId="lease-property-label"
                  label="Property"
                  value={formData.propertyId}
                  onChange={(e) => handleFormChange('propertyId', e.target.value)}
                >
                  {properties.map((property) => (
                    <MenuItem key={property.id} value={property.id}>
                      {property.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="lease-unit-label">Unit</InputLabel>
                <Select
                  labelId="lease-unit-label"
                  label="Unit"
                  value={formData.unitId}
                  onChange={(e) => handleFormChange('unitId', e.target.value)}
                >
                  {(() => {
                    const selectedProperty = properties.find(p => p.id === formData.propertyId);
                    return selectedProperty?.units?.map((unit) => (
                      <MenuItem key={unit.id} value={unit.id}>
                        {unit.name}
                      </MenuItem>
                    ));
                  })()}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="lease-tenant-label">Tenant</InputLabel>
                <Select
                  labelId="lease-tenant-label"
                  label="Tenant"
                  value={formData.tenantId}
                  onChange={(e) => handleFormChange('tenantId', e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select a tenant</em>
                  </MenuItem>
                  {tenants.length === 0 ? (
                    <MenuItem disabled value="__none__">
                      No tenants yet — add one under Tenant Management
                    </MenuItem>
                  ) : (
                    tenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenantDisplayName(tenant)}
                        {tenant.leaseInfo?.propertyId
                          ? ` — ${tenant.propertyName} / ${tenant.unitName}`
                          : ''}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="lease-status-label">Status</InputLabel>
                <Select
                  labelId="lease-status-label"
                  label="Status"
                  value={formData.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                  <MenuItem value="renewal">Renewal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={formData.startDate}
                onChange={(e) => handleFormChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={formData.endDate}
                onChange={(e) => handleFormChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Monthly Rent"
                value={formData.monthlyRent}
                onChange={(e) => handleFormChange('monthlyRent', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Security Deposit"
                value={formData.securityDeposit}
                onChange={(e) => handleFormChange('securityDeposit', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Late Fee"
                value={formData.lateFee}
                onChange={(e) => handleFormChange('lateFee', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              {editingLease?.id ? (
                <EntityDocumentsStrip
                  leaseId={editingLease.id}
                  propertyId={editingLease.propertyId || ''}
                  tenantId={editingLease.tenantId || ''}
                  unitId={editingLease.unitId || ''}
                  entityLabel="Lease"
                  onOpenInHub={
                    typeof onOpenDocuments === 'function'
                      ? () => {
                          handleCloseDialog();
                          onOpenDocuments(editingLease.id);
                        }
                      : null
                  }
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Save the lease first, then you can upload the signed lease or attach files from Documents.
                </Typography>
              )}
            </Grid>

            {isRenewalEligible && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Renewal Options</Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Rent Increase %"
                    value={rentIncreasePercent}
                    onChange={(e) => setRentIncreasePercent(e.target.value)}
                    InputProps={{ inputProps: { min: 0, max: 50, step: 0.5 } }}
                    InputLabelProps={{
                      shrink: true,
                      sx: { whiteSpace: 'nowrap', overflow: 'visible' }
                    }}
                    sx={{ minWidth: 180 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="New Monthly Rent"
                    value={`$${calculatedNewRent.toFixed(2)}`}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeMonthToMonth}
                        onChange={(e) => setIncludeMonthToMonth(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Include month-to-month option in renewal email"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 }, flexWrap: 'wrap' }}>
          {isRenewalEligible && (
            <Button
              onClick={handleSendRenewalNotice}
              variant="outlined"
              color="primary"
              disabled={sendingRenewal}
              startIcon={sendingRenewal ? <CircularProgress size={20} /> : null}
              sx={{ mr: 'auto' }}
            >
              {sendingRenewal ? 'Sending...' : 'Send Renewal Notice'}
            </Button>
          )}
          <Button 
            onClick={handleCloseDialog} 
            disabled={submitting}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {submitting ? 'Saving...' : (editingLease ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {leaseIntakes && (
        <LeaseIntakeDialog
          open={intakeDialog.open}
          resumeIntakeId={intakeDialog.resumeId}
          properties={properties}
          tenants={tenants}
          aiEnabled={leaseIntakes.aiEnabled}
          maxFiles={leaseIntakes.maxFiles}
          onClose={() => setIntakeDialog({ open: false, resumeId: null })}
          onCreated={handleLeaseFromIntake}
          onChanged={fetchLeaseIntakes}
        />
      )}

      {finEnabled && (
        <CreateRentLedgerDialog
          open={Boolean(ledgerLease)}
          lease={ledgerLease}
          onClose={() => setLedgerLease(null)}
          onOpenLedger={(detail) => {
            stashLedgerNav(detail);
            if (typeof onGoToFinancials === 'function') onGoToFinancials();
          }}
        />
      )}
    </Box>
  );
};

export default LeaseManagement; 